import { useDatabase } from "@nozbe/watermelondb/hooks";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useCallback } from "react";
import { ActivityIndicator, Switch } from "react-native";
import { Colors, View } from "react-native-ui-lib";
import { SettingsRootParamList } from "../../../../types";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { LocalDataSummaryCard } from "../../components/LocalDataSummaryCard";
import {
  Card,
  Stack,
  Page,
  FlatActionList,
  useFlashMessages,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import {
  useGlobalSettingsProvider,
  useMutationUpdateGlobalSettings,
} from "../../../../utilities/global-settings/hooks";
import { useRemoveAllProductVariantAndAssociatedData } from "../../../products/utils";
import { ConfirmationPrompt } from "../../../../components/ConfirmationPrompt";
import React from "react";
import { set } from "lodash";

export type DataSettingsScreenProps = NativeStackScreenProps<
  SettingsRootParamList,
  "DataSettingsScreen"
>;

export const DataSettingsScreen = ({ navigation }: DataSettingsScreenProps) => {
  const { updateAppState } = useWithAppState();
  const database = useDatabase();
  const authentication = useAuthenticationProvider();
  const { syncing, fetchAndSyncAllEntities } = useSetupAllStartupEntities();
  const { showMessage } = useFlashMessages();
  const {
    setSetting,
    settings: { salesQueueEnabled },
  } = useLocalSettingsProvider();
  const [promptVisible, setPromptVisible] = React.useState(false);

  const globalSettings = useGlobalSettingsProvider();
  const { updateGlobalSettings } = useMutationUpdateGlobalSettings();
  const { handleDangerouslyDeleteAllProductVariantsAndAssociatedData } =
    useRemoveAllProductVariantAndAssociatedData();
  const { retailerInBoundEnabled } = globalSettings;

  const resetAndLogoutFn = useCallback(() => {
    database
      .write(async () => {
        return database.unsafeResetDatabase().catch((e) => console.log(e));
      })
      .then(() => {
        updateAppState({ terminalId: null, locationId: null });
      })
      .then((i) => {
        authentication.logout(true);
      });
  }, [updateAppState]);

  const deleteDataPrompt = (
    <ConfirmationPrompt
      visible={promptVisible}
      title="Clear all variant data"
      description="Are you sure you want to clear all variant data? This action cannot be undone."
      onCancelPress={() => {
        setPromptVisible(false);
      }}
      onConfirmPress={async () => {
        setPromptVisible(false);
        await handleDangerouslyDeleteAllProductVariantsAndAssociatedData();
      }}
      disabled={false}
    />
  );

  return (
    <Page title="Data Settings">
      <Stack vertical>
        <Card title="Offline Sales Queue">
          <FlatActionList
            items={[
              {
                key: "sales_queue",
                title: "View Sales Queue",
                onPress: () => navigation.navigate("OfflineSalesListScreen"),
              },
              {
                key: "sync_enabled",
                title: "Sales Sync enabled",
                subTitle: "Sales will automatically sync while online",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={salesQueueEnabled}
                        onValueChange={() =>
                          setSetting("salesQueueEnabled", !salesQueueEnabled)
                        }
                      />
                    </View>
                  );
                },
              },
            ]}
          />
        </Card>
        <Card title="Utilities">
          <FlatActionList
            items={[
              {
                key: "variant_data",
                title: "Clear all variant data",
                destructive: true,
                subTitle:
                  "WARNING: Removes all variant groups, options, main product associations and option selections from the system. This does not trigger any changes on connected Retailer systems.",
                onPress: () => setPromptVisible(true),
              },
            ]}
          />
        </Card>
        <Card title="Manage local data">
          <FlatActionList
            items={[
              {
                key: "new_data",
                isAction: true,
                title: "Check for new data",
                disabled: syncing,
                onPress: () =>
                  fetchAndSyncAllEntities()
                    .then((e) => {
                      showMessage({
                        message: "Succesfully checked for new data",
                        severity: "success",
                      });
                    })
                    .catch((err) => console.log(err)),
                componentRight(icon) {
                  return syncing ? (
                    <ActivityIndicator size="small" color={Colors.primary} />
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "reset",
                destructive: true,
                title: "Clear data",
                subTitle:
                  "This will delete all local data permanently, and log you out.",
                onPress: resetAndLogoutFn,
              },
            ]}
          />
        </Card>
        <LocalDataSummaryCard />
      </Stack>
      {deleteDataPrompt}
    </Page>
  );
};
